"use client"

import * as React from "react"
import { Link, useLocation } from "react-router-dom"

import { NavMain } from "@/components/nav-main"
import { NavSecondary } from "@/components/nav-secondary"
import { NavUser } from "@/components/nav-user"
import { WorkspaceSwitcher } from "@/components/team-switcher"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Home01Icon,
  StickyNote02Icon,
  Task01Icon,
  InboxIcon,
  Mic01Icon,
  Settings05Icon,
  SentIcon,
  Files01Icon,
  ArrowRight01Icon,
  Analytics02Icon,
  HelpCircleIcon,
} from "@hugeicons/core-free-icons"
import { useWorkspace } from "@/workspaces/WorkspaceContext"
import { useUser } from "@/auth/UserContext"
import { useProjects } from "@/api/projects/useProjects"
import { useNotificationUnreadCount } from "@/api/notifications/useNotifications"
import { useFeatureFlag } from "@/features/featureFlags"
import { useProductTour } from "@/features/product-tour/product-tour"
import {
  pruneRecentProjectVisits,
  readRecentProjectVisits,
  subscribeRecentProjects,
  type RecentProjectVisit,
} from "@/features/navigation/recentProjects"

const MAX_SHOWN = 5

const PROJECT_COLORS = [
  "#6366f1", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6", "#0ea5e9", "#10b981", "#f97316",
]
function projectColor(index: number) {
  return PROJECT_COLORS[index % PROJECT_COLORS.length]
}

function RecentProjectSubItems({
  recent,
  totalStored,
  projectsPath,
}: {
  recent: RecentProjectVisit[]
  totalStored: number
  projectsPath: string
}) {
  const { pathname } = useLocation()
  const extra = Math.max(0, totalStored - MAX_SHOWN)

  if (recent.length === 0) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild>
          <Link to={projectsPath}>
            <span className="text-muted-foreground">Все проекты</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    )
  }

  return (
    <>
      {recent.map((visit, index) => {
        const url = projectsPath.replace(/\/projects$/, "") + `/projects/${visit.projectId}`
        const isActive = pathname === url || pathname.startsWith(url + "/")
        return (
          <SidebarMenuSubItem key={visit.projectId}>
            <SidebarMenuSubButton asChild isActive={isActive}>
              <Link to={url}>
                <span
                  className="flex size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: projectColor(index) }}
                />
                <span className="truncate">{visit.projectName ?? visit.projectId}</span>
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        )
      })}
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild>
          <Link to={projectsPath}>
            <HugeiconsIcon icon={ArrowRight01Icon} strokeWidth={2} className="size-3.5" />
            <span className="text-muted-foreground">
              Все проекты{extra > 0 && <span className="ml-1 text-xs opacity-60">+{extra}</span>}
            </span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    </>
  )
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { path, workspace } = useWorkspace()
  const { user } = useUser()
  const { projects, isLoading: projectsLoading } = useProjects()
  const meetingsFeature = useFeatureFlag("meetings")
  const { data: unreadData } = useNotificationUnreadCount(workspace.slug)
  const { startTour } = useProductTour()
  const unreadCount = unreadData?.count ?? 0

  const [recent, setRecent] = React.useState<RecentProjectVisit[]>([])
  const [totalStored, setTotalStored] = React.useState(0)

  React.useEffect(() => {
    if (!user || projectsLoading) return
    const validProjectIds = new Set(projects.map((project) => project.id))
    function refresh() {
      const all = readRecentProjectVisits(user!.id, workspace.slug)
      const filtered = all.filter((visit) => validProjectIds.has(visit.projectId))
      setRecent(filtered.slice(0, MAX_SHOWN))
      setTotalStored(filtered.length)
    }
    pruneRecentProjectVisits(user.id, workspace.slug, validProjectIds)
    refresh()
    return subscribeRecentProjects(refresh)
  }, [projects, projectsLoading, user, workspace.slug])

  const navMain = [
    {
      title: "Главная",
      url: "/home",
      icon: <HugeiconsIcon icon={Home01Icon} strokeWidth={2} />,
      tourId: "nav-home",
    },
    {
      title: "Входящие",
      url: "/inbox",
      icon: <HugeiconsIcon icon={InboxIcon} strokeWidth={2} />,
      badge: unreadCount > 0 ? unreadCount : undefined,
      tourId: "nav-inbox",
    },
    {
      title: "Задачи",
      url: "/tasks",
      icon: <HugeiconsIcon icon={Task01Icon} strokeWidth={2} />,
      tourId: "nav-tasks",
    },
    {
      title: "Проекты",
      url: "/projects",
      icon: <HugeiconsIcon icon={StickyNote02Icon} strokeWidth={2} />,
      tourId: "nav-projects",
      defaultOpen: true,
      subContent: (
        <RecentProjectSubItems
          recent={recent}
          totalStored={totalStored}
          projectsPath={path("/projects")}
        />
      ),
    },
    ...(meetingsFeature.enabled
      ? [
          {
            title: "Митинги",
            url: "/meetings",
            icon: <HugeiconsIcon icon={Mic01Icon} strokeWidth={2} />,
            tourId: "nav-meetings",
          },
        ]
      : []),
    {
      title: "Файлы",
      url: "/files",
      icon: <HugeiconsIcon icon={Files01Icon} strokeWidth={2} />,
      tourId: "nav-files",
    },
    {
      title: "Отчёты",
      url: "/reports",
      icon: <HugeiconsIcon icon={Analytics02Icon} strokeWidth={2} />,
      tourId: "nav-reports",
    },

    // {
    //   title: "Календарь",
    //   url: "/calendar",
    //   icon: <HugeiconsIcon icon={Calendar01Icon} strokeWidth={2} />,
    // },
    // {
    //   title: "Уведомления",
    //   url: "/notifications",
    //   icon: <HugeiconsIcon icon={Notification01Icon} strokeWidth={2} />,
    // },
    // {
    //   title: "Команда",
    //   url: "/team",
    //   icon: <HugeiconsIcon icon={UserGroupIcon} strokeWidth={2} />,
    // },
  ]
  const navSecondary = [
    {
      title: "Тур по системе",
      icon: <HugeiconsIcon icon={HelpCircleIcon} strokeWidth={2} />,
      onClick: startTour,
    },
    {
      title: "Настройки",
      url: "/settings",
      icon: <HugeiconsIcon icon={Settings05Icon} strokeWidth={2} />,
    },
    {
      title: "Поддержка",
      url: "/support",
      icon: <HugeiconsIcon icon={SentIcon} strokeWidth={2} />,
    },
  ]

  return (
    <Sidebar variant="sidebar" className="group-data-[state=expanded]:border-r" {...props}>
      <SidebarHeader className="h-[43px] border-b" style={{ padding: 0, gap: 0 }}>
        <WorkspaceSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navMain} />
        <NavSecondary items={navSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter className="h-[43px] border-t" style={{ padding: 0, gap: 0 }}>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  )
}
